import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ENDPOINTS, WEB_URL } from '@/config/endpoints';
import TalkToAI from './talkToRM/talkToAI';

const getSchema = (showExtraEximFields) => {
  return yup.object().shape({
    companyName: yup.string().required("Company Name is required."),
    ...(showExtraEximFields
      ? {
        Name: yup.string().required("This field is required."),
        Email: yup
          .string()
          .required("This field is required.")
          .email("Email is not valid"),
        phoneNumber: yup
          .string()
          .required("This field is required.")
          .matches(/^\d{10}$/, "Please enter a valid 10-digit Phone Number"),
      }
      : {}),
  });
};

const TURNOVER_OPTIONS = [
  { label: '₹5 Cr - ₹25 Cr', value: '₹5Cr-₹25Cr' },
  { label: '₹25 Cr - ₹100 Cr', value: '₹25Cr-₹100Cr' },
  { label: '₹100 Cr - ₹250 Cr', value: '₹100Cr-₹250Cr' },
  { label: '₹250 Cr+', value: '₹250Cr+' }
];

const BUSINESS_TYPES = [
  { name: 'Purchase Invoice Discounting', value: ENDPOINTS.DOMESTIC_MEETING_SCHEDULER },
  { name: 'Sales Invoice Discounting', value: ENDPOINTS.DOMESTIC_MEETING_SCHEDULER },
  { name: 'Export Factoring', value: ENDPOINTS.EXIM_MEETING_SCHEDULER },
  { name: 'Import Factoring', value: ENDPOINTS.EXIM_MEETING_SCHEDULER },
  { name: 'Machinery Loans', value: ENDPOINTS.DOMESTIC_MEETING_SCHEDULER },
  { name: 'Term loans', value: ENDPOINTS.DOMESTIC_MEETING_SCHEDULER },
];

const PRODUCT_OPTIONS = [
  'Automotive & Auto Components',
  'Engineering & Industrial Components',
  'Machinery & Industrial Equipment',
  'Textiles, Apparel & Garments',
  'Handicrafts, Home Décor & Furniture',
  'Food, Agro & Processed Foods',
  'Chemicals & Specialty Chemicals',
  'Pharmaceuticals & Healthcare Products',
  'Metals, Steel & Metal Products',
  'Plastics, Polymers & Rubber Products',
  'Packaging, Paper & Printing Products',
  'Electrical & Electronic Products',
  'Solar, Renewable Energy & Energy Equipment',
  'Building Materials & Construction Products',
  'Leather, Footwear & Accessories',
  'Consumer & FMCG Products',
  'Others',
];

const CalendlyBadge = ({ PageMeetingURL = false }) => {
  const router = useRouter();
  const { locale } = router;
  const [isOpen, setIsOpen] = useState(false);
  const [turnover, setTurnover] = useState(null);
  const [businessType, setBusinessType] = useState(null);
  const [product, setProduct] = useState(null);
  const [otherProduct, setOtherProduct] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const productDropdownRef = useRef(null);
  const containerRef = useRef(null);

  const isInlinePage = router.pathname.startsWith('/hsn-code') || router.pathname.includes('/blogs') || router.pathname.includes('/articles') || router.pathname.includes('/thankyou-response');
  const isHomepage = router.pathname === '/';

  const eximPageURLs = router.pathname == "/export-factoring" || router.pathname == "/global-buyer" || router.pathname == "/shipment-calculator" ||
    router.pathname == "/export-factoring/buyer-credit-check" || router.pathname == "/freight-invoice-discounting-india" ||
    router.pathname == "/machinery-loans-for-exporters-india" || router.pathname == "/import-factoring" || router.pathname.startsWith('/credlix-finsight')
    || router.pathname == "/export-factoring-interest-subvention";

  const isExim = eximPageURLs || (isHomepage && businessType?.value === ENDPOINTS.EXIM_MEETING_SCHEDULER);

  const showExtraEximFields = isExim && turnover?.value === '₹5Cr-₹25Cr';

  const {
    register,
    handleSubmit,
    reset,
    watch,
    unregister,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(getSchema(showExtraEximFields)),
  });

  useEffect(() => {
    if (!showExtraEximFields) {
      unregister('Name');
      unregister('Email');
      unregister('phoneNumber');
    }
  }, [showExtraEximFields, unregister]);

  const companyName = watch('companyName');

  useEffect(() => {
    const subscription = watch(() => {
      if (errorMessage) {
        setErrorMessage('');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, errorMessage]);

  const resetForm = () => {
    setIsOpen(false);
    setTurnover(null);
    setBusinessType(null);
    setProduct(null);
    setOtherProduct('');
    setIsDropdownOpen(false);
    setIsProductDropdownOpen(false);
    setIsSubmitted(false);
    setSubmittedData(null);
    setErrorMessage('');
    setIsLoading(false);
    reset();
  };

  const handleProductSelect = (option) => {
    setProduct(option);
    setIsProductDropdownOpen(false);
    if (errorMessage) {
      setErrorMessage('');
    }
    if (option !== 'Others') {
      setOtherProduct('');
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const header = document.querySelector('header');
    if (isInlinePage && header) {
      header.style.zIndex = '0';
    }

    const parentEl = containerRef.current?.parentElement;
    if (isInlinePage && parentEl) {
      parentEl.style.zIndex = '99999';
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        resetForm();
      }
    };

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setIsProductDropdownOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.body.style.overflow = '';
      if (isInlinePage && header) {
        header.style.zIndex = '';
      }
      if (isInlinePage && parentEl) {
        parentEl.style.zIndex = '';
      }
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isInlinePage]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.isMeetingPopupOpen = isOpen;
      if (isOpen) {
        window.dispatchEvent(new CustomEvent('meetingPopupOpen'));
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isDropdownOpen && dropdownRef.current) {
      setTimeout(() => {
        dropdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isProductDropdownOpen && productDropdownRef.current) {
      setTimeout(() => {
        productDropdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }, [isProductDropdownOpen]);

  if (locale !== 'en-in') {
    return null;
  }

  if (PageMeetingURL) {
    if (!isInlinePage) return null;
  } else {
    if (isInlinePage) return null;
  }

  const meetingSchedulerURL = PageMeetingURL
    ? ENDPOINTS.DOMESTIC_MEETING_SCHEDULER
    : (eximPageURLs ? ENDPOINTS.EXIM_MEETING_SCHEDULER : ENDPOINTS.DOMESTIC_MEETING_SCHEDULER);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      resetForm();
    }
  };

  const getFinalProductName = () => {
    if (!product) return '';
    if (product === 'Others') return otherProduct.trim();
    return product;
  };

  const productName = getFinalProductName();
  const formValidationPassed = showExtraEximFields ? isValid : true;
  const isFormValid = isHomepage
    ? (turnover && businessType && companyName?.trim() && productName && formValidationPassed)
    : (turnover && companyName?.trim() && productName && formValidationPassed);

  const handleRedirect = (data) => {
    if (!productName) return;
    const companyNameVal = data?.companyName || companyName;

    if (isHomepage) {
      if (turnover && businessType && companyNameVal?.trim()) {
        const redirectUrl = `${businessType.value}&turnover=${turnover.value}&pageUrl=${WEB_URL + router.asPath}&companyName=${companyNameVal}&product=${productName}`;
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        resetForm();
      }
    } else {
      if (turnover && companyNameVal?.trim()) {
        const redirectUrl = `${meetingSchedulerURL}&turnover=${turnover.value}&pageUrl=${WEB_URL + router.asPath}&companyName=${companyNameVal}&product=${productName}`;
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        resetForm();
      }
    }
  };

  const onSubmit = async (data) => {
    if (!productName) return;

    if (showExtraEximFields) {
      setErrorMessage('');
      setIsLoading(true);
      try {
        let apiKey = '';
        try {
          const keyRes = await fetch(ENDPOINTS.GENERATE_API_KEY, {
            method: 'GET',
            headers: {
              'api-key': ENDPOINTS.HEADER_API_KEY,
            },
          });
          const keyData = await keyRes.json();
          if (keyData?.result) {
            apiKey = keyData.result;
          }
        } catch (keyError) {
          console.error('Error fetching dynamic API key:', keyError);
        }
        const payload = {
          spoc1Name: data?.Name,
          spoc1Email: data?.Email,
          spoc1PhoneNumber: data?.phoneNumber,
          exporterName: data?.companyName,
          exporterGstNo: '',
          exporterCity: '',
          businessType: 'EXIM',
          country: 'India',
          leadSource: 'ATOM_BOOK_A_MEETING',
          campaignName: 'ATOM_BOOK_A_MEETING',
          annualTurnover: turnover?.value || '',
          industryType: productName,
        };
        console.log(payload, 'payload');
        const response = await fetch(ENDPOINTS.CREATE_LEAD, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-key': apiKey,
          },
          body: JSON.stringify(payload),
        });
        const resData = await response.json().catch(() => null);
        if (response.status === 200 && (!resData?.status || resData?.status === 200)) {
          setSubmittedData(data);
          setIsSubmitted(true);
        } else {
          setErrorMessage(resData?.message || resData?.error || 'Something went wrong. Please try again.');
        }
      } catch (error) {
        console.error('Error creating lead:', error);
        setErrorMessage(error?.message || 'Something went wrong. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      handleRedirect(data);
    }
  };

  return (
    <div ref={containerRef}>
      {PageMeetingURL ? (
        <button
        onClick={() => setIsOpen(true)}
        className="group flex gap-xsp justify-center items-center mb-3 mx-auto rounded-2xl w-3/4 py-1 border border-brand
        hover:border-transparent
        hover:bg-gradient-to-r hover:from-[#022B5C] hover:to-[#0363C4]
        hover:text-white transition-all duration-300"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="24"
          height="24"
          className="fill-brand group-hover:fill-white transition-colors duration-300"
        >
          <path d="M17 3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9V3H15V1H17V3ZM4 9V19H20V9H4ZM6 13H11V17H6V13Z" />
        </svg>
      
        <p className="text-base2 font-medium text-brand group-hover:text-white ml-xsp transition-colors duration-300">
          Book a meeting
        </p>
      </button>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="
            fixed bottom-3 right-1 z-50
            block w-fit
            rounded-3xl
            bg-gradient-to-r from-[#022B5C] to-[#0363C4]
            px-3 py-1
            text-[16px] font-medium text-white
            shadow-md
            transition-all duration-300 ease-in-out
            hover:from-[#0363C4] hover:to-[#022B5C]
          "
        >
          Book a meeting
        </button>
      )}

      {isOpen && (
        <div
          onClick={handleOverlayClick}
          className="fixed inset-0 z-[100] flex min-h-full items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
        >
          <div className={`relative w-full ${isHomepage ? "max-w-md" : "max-w-md"} 
            rounded-2xl bg-white shadow-2xl transition-all duration-300 flex flex-col animate-fade-in my-auto`}
          >
            <button
              onClick={resetForm}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 focus:outline-none"
              aria-label="Close modal"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-4 sm:p-3">
              {isSubmitted ? (
                <TalkToAI formData={submittedData} onClose={resetForm} />
              ) : (
                <>
                  <div className="mx-auto flex items-center justify-center rounded-full bg-blue-50">
                    <svg className="h-5 w-5 text-[#0363C4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="mt-xsp text-center text-lg sm:text-base2 font-medium text-gray-900">
                    Book a Meeting
                  </h3>

                  {/* Select Business Type (ONLY on homepage) */}
                  {isHomepage && (
                    <div className="my-1">
                      <label className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                        Select a Product*
                      </label>
                      <div className="flex flex-wrap gap-[7px]">
                        {BUSINESS_TYPES.map((option) => {
                          const isSelected = businessType && businessType.name === option.name;
                          return (
                            <button
                              key={option.name}
                              type="button"
                              onClick={() => setBusinessType(option)}
                              className={`flex items-center justify-center text-center py-[8px] px-1 text-xs sm:text-xxs font-medium rounded-full border transition-all leading-tight cursor-pointer ${isSelected
                                ? 'bg-gradient-to-r from-[#022B5C] to-[#0363C4] hover:from-[#0363C4] hover:to-[#022B5C] text-white border-brand'
                                : 'bg-white text-gray-700 border-brand'
                                }`}
                            >
                              {option.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="mt-1">
                    <label htmlFor="companyName" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                      Company Name*
                    </label>
                    <div className="mt-0">
                      <input
                        {...register("companyName")}
                        type="text"
                        id="companyName"
                        placeholder="Enter Company Name"
                        className="block w-full appearance-none rounded-md border border-bordercolor py-2.5 px-2 placeholder-gray-900 text-sm focus:outline-none"
                      />
                      {errors.companyName?.message && (
                        <p className="text-progressColor4 text-xs mt-[3px]">
                          {errors.companyName?.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Product / Industry Dropdown */}
                  <div className="mt-1">
                    <label htmlFor="product" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                      Select Your Industry*
                    </label>
                    <div className="relative" ref={productDropdownRef}>
                      <button
                        id="product"
                        type="button"
                        onClick={() => {
                          setIsProductDropdownOpen(!isProductDropdownOpen);
                          setIsDropdownOpen(false);
                        }}
                        className={`flex w-full items-center justify-between bg-white py-2.5 px-2 pr-10 text-sm text-gray-700 rounded-md border transition-all focus:outline-none focus:ring-1 focus:ring-[#0363C4] ${isProductDropdownOpen ? 'border-[#022B5C] ring-1 ring-[#0363C4]' : 'border-bordercolor'
                          }`}
                      >
                        <span className={product ? 'text-gray-900 sm:text-xs' : 'text-gray-400'}>
                          {product || ' Select Your Industry'}
                        </span>
                      </button>
                      <div className="pointer-events-none absolute right-0 top-0 bottom-0 flex items-center px-2 text-gray-400">
                        <svg
                          className={`h-3 w-3 transition-transform duration-200 ${isProductDropdownOpen ? 'rotate-180' : ''}`}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {isProductDropdownOpen && (
                        <div className="absolute z-30 mt-0.5 w-full rounded-md bg-white border border-bordercolor shadow-lg max-h-44 overflow-y-auto">
                          <ul className="py-1">
                            {PRODUCT_OPTIONS.map((option) => (
                              <li key={option}>
                                <button
                                  type="button"
                                  onClick={() => handleProductSelect(option)}
                                  className={`w-full text-left py-1.5 px-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150 ${product === option ? 'bg-blue-50/50 font-medium text-[#0363C4]' : ''
                                    }`}
                                >
                                  {option}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Custom Product Text Input (Shown when 'Others' is selected) */}
                  {product === 'Others' && (
                    <div className="mt-1">
                      <label htmlFor="otherProduct" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                        Specify Your Industry*
                      </label>
                      <input
                        name="otherProduct"
                        type="text"
                        id="otherProduct"
                        value={otherProduct}
                        onChange={(e) => {
                          setOtherProduct(e.target.value);
                          if (errorMessage) setErrorMessage('');
                        }}
                        placeholder="Enter Your Industry name"
                        className="block w-full appearance-none rounded-md border border-bordercolor py-2.5 px-2 placeholder-gray-400 text-sm focus:outline-none"
                      />
                    </div>
                  )}

                  {/* Annual Turnover (Dropdown Select Box) */}
                  <div className="mt-1">
                    <label htmlFor="turnover" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                      Annual Turnover*
                    </label>
                    <div className="relative" ref={dropdownRef}>
                      <button
                        id="turnover"
                        type="button"
                        onClick={() => {
                          setIsDropdownOpen(!isDropdownOpen);
                          setIsProductDropdownOpen(false);
                        }}
                        className={`flex w-full items-center justify-between bg-white py-2.5 px-2 pr-10 text-sm text-gray-700 rounded-md border transition-all focus:outline-none focus:ring-1 focus:ring-[#0363C4] ${isDropdownOpen ? 'border-[#022B5C] ring-1 ring-[#0363C4]' : 'border-bordercolor'
                          }`}
                      >
                        <span className={turnover ? 'text-gray-900' : 'text-gray-400'}>
                          {turnover ? turnover.label : 'Select Turnover'}
                        </span>
                      </button>
                      <div className="pointer-events-none absolute right-0 top-0 bottom-0 flex items-center px-2 text-gray-400">
                        <svg
                          className={`h-3 w-3 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {isDropdownOpen && (
                        <div className="absolute z-20 mt-0.5 w-full rounded-md bg-white border border-bordercolor shadow-lg max-h-44 overflow-y-auto">
                          <ul className="py-1">
                            {TURNOVER_OPTIONS.map((option) => (
                              <li key={option.value}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTurnover(option);
                                    setIsDropdownOpen(false);
                                    if (errorMessage) setErrorMessage('');
                                  }}
                                  className={`w-full text-left py-1.5 px-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150 ${turnover && turnover.value === option.value ? 'bg-blue-50/50 font-medium text-[#0363C4]' : ''
                                    }`}
                                >
                                  {option.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {showExtraEximFields && (
                    <>
                      <div className="mt-1">
                        <label htmlFor="name" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                          Enter Your Name*
                        </label>
                        <input
                          {...register("Name")}
                          type="text"
                          id="name"
                          placeholder="Enter Your Name"
                          className="block w-full appearance-none rounded-md border border-bordercolor py-2.5 px-2 placeholder-gray-900 text-sm focus:outline-none"
                        />
                        {errors.Name?.message && (
                          <p className="text-progressColor4 text-xs mt-0.5">
                            {errors.Name?.message}
                          </p>
                        )}
                      </div>

                      <div className="mt-1">
                        <label htmlFor="emailID" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                          Enter Your Email ID*
                        </label>
                        <input
                          {...register("Email")}
                          type="email"
                          id="emailID"
                          placeholder="Enter Your Email ID"
                          className="block w-full appearance-none rounded-md border border-bordercolor py-2.5 px-2 placeholder-gray-900 text-sm focus:outline-none"
                        />
                        {errors.Email?.message && (
                          <p className="text-progressColor4 text-xs mt-0.5">
                            {errors.Email?.message}
                          </p>
                        )}
                      </div>

                      <div className="mt-1">
                        <label htmlFor="phoneNumber" className="block text-sm sm:text-sm font-medium text-gray-500 mb-xsp">
                          Enter Your Phone Number*
                        </label>
                        <input
                          {...register("phoneNumber")}
                          type="tel"
                          inputMode="tel"
                          maxLength={10}
                          id="phoneNumber"
                          placeholder="Enter Your Phone Number"
                          className="block w-full appearance-none rounded-md border border-bordercolor py-2.5 px-2 placeholder-gray-900 text-sm focus:outline-none"
                        />
                        {errors.phoneNumber?.message && (
                          <p className="text-progressColor4 text-xs mt-0.5">
                            {errors.phoneNumber?.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  <div className="mt-2">
                    <button
                      onClick={handleSubmit(onSubmit)}
                      disabled={!isFormValid || isLoading}
                      className="w-full py-2.5 rounded-md text-white font-medium text-sm shadow-md transition-all duration-300 ease-in-out bg-gradient-to-r from-[#022B5C] to-[#0363C4] hover:from-[#0363C4] hover:to-[#022B5C] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Submitting...
                        </div>
                      ) : showExtraEximFields ? (
                        'Submit'
                      ) : (
                        'Proceed to Schedule'
                      )}
                    </button>
                    {errorMessage && (
                      <p className="text-progressColor4 text-xs text-center mt-1 font-medium">
                        {errorMessage}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendlyBadge;